import React, { useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import styles from "./TradeCallsForm.module.css";
import { axiosConfig } from "../../../../../axios/axiosConfig";
import useDebounce from "../../../../../utils/common/useDebounce";
import MuiModal from "../../../../muiComps/MuiModal";
import MultiPlansSelectCheckBox from "../../../../common/MultiPlansSelectCheckBox";
import TradeCallsFormSummaryModal from "./TradeCallsFormSummaryModal";
import { isFuturesType, getTradeUpdateUrl } from "../tradeTypes";
import usePartnersWS from "../../../../../hooks/usePartnersWS";

// const advisoryTypeOptions = [
//   { value: "SHORT", label: "SHORT" },
//   { value: "MEDIUM", label: "MEDIUM" },
//   { value: "LONG", label: "LONG" },
// ];

const segmentOptions = [
  { id: 1, label: "INTRADAY", value: "INTRADAY" },
  { id: 2, label: "BTST", value: "BTST" },
  { id: 3, label: "SWING", value: "SWING" },
  // { id: 4, label: "STBT", value: "STBT" },
];

const entryPriceRangeOptions = [
  { value: "PRICE_RANGE", label: "PRICE RANGE" },
  { value: "FIXED_PRICE", label: "FIXED PRICE" },
  { value: "CMP", label: "CMP" },
];

const instrumentOptions = [
  { value: "STOCK_FUTURE", label: "STOCK FUTURE" },
  { value: "INDEX_FUTURE", label: "INDEX FUTURE" },
];

const parravaCallTypeOptions = [
  { value: "FUNDAMENTAL", label: "FUNDAMENTAL" },
  { value: "TECHNICAL", label: "TECHNICAL" },
];

const signalOptions = {
  cash: [
    { id: 2, label: "BUY", value: "Buy" },
    { id: 3, label: "SELL", value: "Sell" },
  ],
  default: [
    { id: 2, label: "LONG", value: "long" },
    { id: 3, label: "SHORT", value: "short" },
  ],
};

const pnlBoxStyle = (isPositive) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "6px 10px",
  borderRadius: "6px",
  backgroundColor: isPositive ? "#e6f4ea" : "#fdecea",
  color: isPositive ? "#1a7f3c" : "#c0392b",
  fontSize: "13px",
  border: `1px solid ${isPositive ? "#a8d5b5" : "#f5c6c2"}`,
});


// IMPORTANT: This is a common form to create 'cash', 'futures' and 'options' trades.
const TradeCallsForm = ({
  isEditing,
  setIsEditing,
  tradesFormInput,
  setTradesFormInput,
  selectedTradeType,
  // initialAdvisoryFormInput,
  clearTradesFormInput,
  activeAdvisoryObj,
  exitPriceRef,
  handleCloseFormModal,
}) => {
  const { user } = useSelector((state) => state.auth);
  const USERID = user?.userDetails?.id;

  const [selectedPlans, setSelectedPlans] = useState([]); // list of Plans ID that are eligible for particular advisory
  const formattedSelectedPlans = selectedPlans.join(",");

  const [openAdvisorySummaryModal, setOpenAdvisorySummaryModal] =
    useState(false);

  // const [symbolsList, setSymbolsList] = useState([]);

  const [fileInput, setFileInput] = useState(null); // pdf file input
  const [selectedExpiryDate, setSelectedExpiryDate] = useState(""); // selected expiry date for futures
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [ltpRefreshing, setLtpRefreshing] = useState(false);

  const refreshLtp = async () => {
    if (ltpRefreshing || !symbolName) return;
    setLtpRefreshing(true);
    try {
      if (selectedTradeType === "cash") {
        const res = await axiosConfig({
          method: "get",
          url: `/get-latest-tick/`,
          params: { symbol: symbolName },
        });
        const data = res.data;
        if (data?.status === "success" && data?.data?.ltp) {
          setTradesFormInput((prev) => ({ ...prev, ltp: data.data.ltp }));
        }
      } else if (isFuturesType(selectedTradeType) && selectedExpiryDate) {
        const response = await fetchFutSymbolDetails(symbolName, selectedExpiryDate);
        const newLtp = response?.response?.ltp;
        if (newLtp) {
          setTradesFormInput((prev) => ({ ...prev, ltp: newLtp }));
        }
      }
    } catch (err) {
      toast.error("Failed to refresh LTP");
    } finally {
      setLtpRefreshing(false);
    }
  };

  //   const DURATION = {
  //     SHORT: "0-3 MONTHS",
  //     MEDIUM: "6-12 MONTHS",
  //     LONG: "12-36 MONTHS",
  //   };

  const {
    symbolName,
    segment,
    signal,
    entryPrice,
    targetPrice,
    stopLoss,
    exitPrice,
    lotSize,
    lotExpiry,
    call_type,
    bid_type,
    ltp,
    action_type, // "edit", "cancel", or "exit"
    instrument,
    entry_price_range,
    valid_for_parrva,
    parrva_call_type,
    stock_horizon,
    trade_type,
    fut_symbol,
    // profitLoss,
    // tradeClosedInDays,
  } = tradesFormInput;
  const debouncedExitPrice = useDebounce(exitPrice, 600);

  // LIVE LTP (websocket) — cash subscribes by symbol name, futures/future-pro by contract symbol
  const wsSymbol = isFuturesType(selectedTradeType) ? fut_symbol : symbolName;
  const { ltp: wsLtp, isConnected: wsConnected } = usePartnersWS({
    symbol: wsSymbol,
    enabled: !!wsSymbol,
  });
  // Until the first websocket tick arrives for the newly selected symbol, fall back
  // to the already-fetched REST LTP so "Live LTP" isn't blank right after selection
  const liveLtp = wsLtp != null ? wsLtp : (ltp ? parseFloat(ltp) : null);

  // ✅ AUTO SELECT BUY WHEN SEGMENT IS SWING
  useEffect(() => {
    if (segment === "SWING" && !isEditing) {
      setTradesFormInput((prev) => ({
        ...prev,
        signal: selectedTradeType === "cash" ? "Buy" : "long",
      }));
    }
  }, [segment, selectedTradeType, isEditing]);

  // ✅ Futures Pro: STBT defaults to short, BTST defaults to long
  useEffect(() => {
    if (selectedTradeType !== "future-pro" || isEditing) return;
    if (segment === "STBT") {
      setTradesFormInput((prev) => ({ ...prev, signal: "short" }));
    } else if (segment === "BTST") {
      setTradesFormInput((prev) => ({ ...prev, signal: "long" }));
    }
  }, [segment, selectedTradeType, isEditing]);

  //   const ADVISORY_DURATION = DURATION[tradesFormInput.advisoryType];
  const queryClient = useQueryClient();

  // Numeric fields — block any non-digit/non-decimal-point characters from being typed
  const NUMERIC_FIELDS = ["entryPrice", "targetPrice", "stopLoss", "exitPrice"];

  const formInputOnChange = (event) => {
    const { name } = event.target;
    const rawValue = event.target.value;
    const value = NUMERIC_FIELDS.includes(name)
      ? rawValue.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
      : rawValue;

    setTradesFormInput((prevState) => {
      if (name === "exitPrice" && value.trim() !== "") {
        return { ...prevState, exitPrice: value, trade_status: "CLOSED" };
      }

      if (name === "call_type" && value === "CMP") {
        return {
          ...prevState,
          call_type: value,
        };
      }

      // Auto-fill lot_size when symbol is selected in futures trade type
      if (name === "symbolName" && isFuturesType(selectedTradeType)) {
        const selectedSymbol = futureSymbolsList.find(
          (symbol) => symbol.name === value,
        );
        if (selectedSymbol && selectedSymbol.lot_size) {
          return {
            ...prevState,
            symbolName: value,
            lotSize: selectedSymbol.lot_size.toString(),
          };
        }
      }

      return {
        ...prevState,
        [event.target.name]:
          name === "symbolName" ? value.toUpperCase() : value,
      };
    });
  };

  const handleParravaToggle = (event) => {
    setTradesFormInput((prevState) => ({
      ...prevState,
      valid_for_parrva: event.target.checked,
    }));
  };

  useEffect(() => {
    if (isEditing && exitPriceRef.current) {
      exitPriceRef.current.focus();
    }
  }, [isEditing]);

  const fileInputOnChange = (event) => {
    setFileInput(event.target.files[0]);
  };

  // Handle expiry date change
  const handleExpiryChange = (event) => {
    const newExpiry = event.target.value;
    setSelectedExpiryDate(newExpiry);
    setTradesFormInput((prevState) => ({
      ...prevState,
      lotExpiry: newExpiry,
    }));
  };

  // FETCH SYMBOLS LIST QUERY
  const { data: symbolsData, isLoading } = useQuery({
    retry: false,
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchInterval: false, // Disable automatic refetching
    queryKey: ["symbols-list"], // dependency
    queryFn: () => fetchSymbolsList(),
    // onSuccess: (data) => {
    //   setSymbolsList(data.response[0].data);
    // },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Something went wrong");
    },
  });

  const symbolsList = symbolsData?.response?.[0]?.data || [];

  // FETCH FUTURES SYMBOLS LIST QUERY
  const { data: futureSymbolsData, isLoading: isFutureSymbolsLoading } =
    useQuery({
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      enabled: isFuturesType(selectedTradeType), // only fetch when futures trade type is selected
      queryKey: ["futures-symbols-list"],
      queryFn: () => fetchFutureSymbolsList(),
      onError: (error) => {
        toast.error(
          error?.response?.data?.response || "Failed to fetch futures symbols",
        );
      },
    });
  const futureSymbolsList = futureSymbolsData?.response || [];
  console.log(futureSymbolsData, "futureSymbolsList");
console.log(futureSymbolsList, "futureSymbolsList1");

  // Use futures symbols list when trade type is "futures", otherwise use regular cash symbols
  const displaySymbolsList =
    isFuturesType(selectedTradeType) ? futureSymbolsList : symbolsList;
  console.log(displaySymbolsList , "symbolsList")
  console.log(futureSymbolsList, "symbolsList1")
  console.log(symbolsList, "symbolsList2")

  // FETCH EXPIRY DATES QUERY (only for futures when symbol is selected)
  const { data: expiryDatesData, isLoading: isExpiryDatesLoading } = useQuery({
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    enabled: isFuturesType(selectedTradeType) && !!symbolName && !isEditing, // only fetch when futures + symbol selected
    queryKey: ["expiry-dates", symbolName],
    queryFn: () => fetchExpiryDates(symbolName),
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to fetch expiry dates",
      );
    },
  });

  const expiryDatesList = expiryDatesData?.response || [];

  // AUTO-SELECT FIRST (LATEST) EXPIRY DATE when expiry dates list loads
  useEffect(() => {
    if (
      isFuturesType(selectedTradeType) &&
      expiryDatesList.length > 0 &&
      !isEditing
    ) {
      const latestExpiry = expiryDatesList[0]; // First date is the latest
      setSelectedExpiryDate(latestExpiry);
      setTradesFormInput((prevState) => ({
        ...prevState,
        lotExpiry: latestExpiry,
      }));
    }
  }, [expiryDatesList, selectedTradeType, isEditing, setTradesFormInput]);

  // AUTO-FILL LOT SIZE on initial load when futures symbols list is fetched
  useEffect(() => {
    if (
      isFuturesType(selectedTradeType) &&
      futureSymbolsList.length > 0 &&
      symbolName &&
      !lotSize &&
      !isEditing
    ) {
      const selectedSymbol = futureSymbolsList.find(
        (symbol) => symbol.name === symbolName,
      );
      if (selectedSymbol && selectedSymbol.lot_size) {
        setTradesFormInput((prevState) => ({
          ...prevState,
          lotSize: selectedSymbol.lot_size.toString(),
        }));
      }
    }
  }, [
    futureSymbolsList,
    symbolName,
    selectedTradeType,
    isEditing,
    lotSize,
    setTradesFormInput,
  ]);

  // FETCH CASH LTP when symbol is selected
  useEffect(() => {
    if (selectedTradeType !== "cash" || !symbolName || isEditing) return;

    const fetchCashLtp = async () => {
      try {
        const res = await axiosConfig({
          method: "get",
          url: `/get-latest-tick/`,
          params: { symbol: symbolName },
        });
        const data = res.data;
        if (data?.status === "success" && data?.data?.ltp) {
          setTradesFormInput((prev) => ({ ...prev, ltp: data.data.ltp }));
        }
      } catch (err) {
        console.error("Failed to fetch cash LTP:", err);
      }
    };

    fetchCashLtp();
  }, [symbolName, selectedTradeType, isEditing, setTradesFormInput]);

  // FETCH FUTURES SYMBOL DETAILS (fut_symbol and LTP) when symbol + expiry are available
  useEffect(() => {
    if (
      isFuturesType(selectedTradeType) &&
      symbolName &&
      selectedExpiryDate &&
      !isEditing
    ) {
      const fetchFutDetails = async () => {
        try {
          const response = await fetchFutSymbolDetails(
            symbolName,
            selectedExpiryDate,
          );
          const futSymbol = response?.response?.symbol;
          const ltp = response?.response?.ltp;

          if (futSymbol) {
            setTradesFormInput((prevState) => ({
              ...prevState,
              fut_symbol: futSymbol,
              ltp: ltp || "",
            }));
          }
        } catch (error) {
          console.error("Failed to fetch futures symbol details:", error);
          toast.error("Failed to fetch LTP. Please try again.");
        }
      };

      fetchFutDetails();
    }
  }, [
    symbolName,
    selectedExpiryDate,
    selectedTradeType,
    isEditing,
    setTradesFormInput,
  ]);

  // POST ADVISORY FORM TO BACKEND
  const postTradesFormMutation = useMutation({
    mutationFn: () =>
      postTradesForm(
        USERID,
        tradesFormInput,
        fileInput,
        formattedSelectedPlans,
        selectedTradeType,
      ),
    onSuccess: (data) => {
      toast.success(data?.response);
      // window.location.reload();
      queryClient.invalidateQueries({ queryKey: ["calls-list"] });
      queryClient.invalidateQueries({ queryKey: ["open-trades"] });
      queryClient.invalidateQueries({ queryKey: ["closed-trades"] });
      clearTradesFormInput();
      setFileInput(null);
      setIsEditing(false);
      if (handleCloseFormModal) handleCloseFormModal();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.response ||
          error?.response?.data?.message ||
          "Something went wrong",
      );
    },
  });

  //   submit advisory form handler
  const handlePublish = () => {
    if (!signal || signal.trim() === "") {
      toast.error("Please select a signal");
      return;
    }
    postTradesFormMutation.mutate();
    setOpenAdvisorySummaryModal(false);
  };

  // EDIT/UPDATE ADVISORY FORM TO BACKEND
  // const updateTradesFormMutation = useMutation({
  //   mutationFn: () =>
  //     updateTradesForm(
  //       USERID,
  //       tradesFormInput,
  //       activeAdvisoryObj,
  //       selectedTradeType
  //     ),
  //   onSuccess: (data) => {
  //     toast.success(data?.response);
  //     window.location.reload();
  //   },
  //   onError: (error) => {
  //     toast.error(error?.response?.data?.response || "Something went wrong");
  //   },
  //   onSettled: () => {
  //     // setTradesFormInput(initialAdvisoryFormInput);
  //     clearTradesFormInput();
  //     setFileInput(null);
  //     setIsEditing(false);
  //   },
  // });

  const updateTradesFormMutation = useMutation({
    mutationFn: () =>
      updateTradesForm(
        USERID,
        tradesFormInput,
        fileInput,
        activeAdvisoryObj,
        selectedTradeType,
      ),
    onSuccess: (data) => {
      toast.success(data?.response || "Exit updated successfully");
      queryClient.invalidateQueries({ queryKey: ["calls-list"] });
      queryClient.invalidateQueries({ queryKey: ["open-trades"] });
      queryClient.invalidateQueries({ queryKey: ["closed-trades"] });
      clearTradesFormInput();
      setFileInput(null);
      setIsEditing(false);
      if (handleCloseFormModal) handleCloseFormModal();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.response ||
          error?.response?.data?.message ||
          "Something went wrong",
      );
    },
  });

  // EDIT / UPDATE ADVISORY FORM
  const handleUpdate = () => {
    updateTradesFormMutation.mutate();
    setOpenAdvisorySummaryModal(false);
    // setIsEditing(false);
  };

  //   submit advisory form handler
  const handleCloseModal = () => {
    setOpenAdvisorySummaryModal(false);
  };

  // CANCEL EDIT HANDLER
  const handleCancelEdit = () => {
    setIsEditing(false);
    clearTradesFormInput();
    // Close the modal
    if (handleCloseFormModal) handleCloseFormModal();
  };

  const DISABLE_CANCEL_EDIT_BTN = !exitPrice || exitPrice.trim() === "";

  // Direction-aware Target/Stoploss check, same rule across cash, futures and future-pro:
  // Buy/Long  -> Stoploss < Entry < Target
  // Sell/Short -> Target < Entry < Stoploss
  const handleViewSummary = () => {
    const skipValidation = isEditing && (action_type === "cancel" || action_type === "exit");
    if (!skipValidation) {
      const baseEntry = parseFloat(call_type === "CMP" ? ltp : entryPrice);
      const t = parseFloat(targetPrice);
      const sl = parseFloat(stopLoss);
      const isShort = ["sell", "short"].includes(signal?.toLowerCase());

      if (!isNaN(baseEntry) && baseEntry > 0) {
        if (!isNaN(t) && t > 0) {
          const targetInvalid = isShort ? t >= baseEntry : t <= baseEntry;
          if (targetInvalid) {
            toast.error(
              isShort
                ? "Target Price must be less than Entry Price"
                : "Target Price must be greater than Entry Price"
            );
            return;
          }
        }
        if (!isNaN(sl) && sl > 0) {
          const slInvalid = isShort ? sl <= baseEntry : sl >= baseEntry;
          if (slInvalid) {
            toast.error(
              isShort
                ? "Stop Loss must be greater than Entry Price"
                : "Stop Loss must be less than Entry Price"
            );
            return;
          }
        }
      }
    }
    setOpenAdvisorySummaryModal(true);
  };

  // Auto-fetch P&L when debounced exit price changes (cash/futures + exit mode only)
  useEffect(() => {
    const isCash = selectedTradeType === "cash";
    const isFutures = isFuturesType(selectedTradeType);
    if (
      (!isCash && !isFutures) ||
      action_type !== "exit" ||
      !debouncedExitPrice ||
      debouncedExitPrice.trim() === "" ||
      !activeAdvisoryObj?.id
    ) {
      setPnlData(null);
      return;
    }
    const fetchPnl = async () => {
      setPnlLoading(true);
      try {
        const url = isFutures
          ? "/predictions/check-pnl-future/"
          : "/predictions/check-pnl/";
        const res = await axiosConfig({
          method: "get",
          url,
          params: {
            call_id: activeAdvisoryObj.id,
            exit_price: debouncedExitPrice,
          },
        });
        setPnlData(res?.data?.response);
      } catch (err) {
        console.error("P&L check failed:", err);
        setPnlData(null);
      } finally {
        setPnlLoading(false);
      }
    };
    fetchPnl();
  }, [
    debouncedExitPrice,
    selectedTradeType,
    action_type,
    activeAdvisoryObj?.id,
  ]);

  // Validation for Update button based on action_type
  const getDisableUpdateBtn = () => {
    if (action_type === "cancel") {
      // Cancel action: must have cancel_reason
      return (
        !tradesFormInput.cancel_reason ||
        tradesFormInput.cancel_reason.trim() === ""
      );
    }
    if (action_type === "exit") {
      return !exitPrice || exitPrice.trim() === "";
    }
    // Edit action: no special validation
    return false;
  };
  const DISABLE_UPDATE_BTN = getDisableUpdateBtn();

  return (
    <div className={styles.StockAdvisoryForm}>
      {/* <h1>StockAdvisoryForm</h1> */}
      {/* {isEditing ? <span className={styles.blinkingText} >Editing!!</span>  : ""} */}

      {/* this wrapper holds multiselect plan list and advisory form */}
      <div className={styles.outerFormWrapper}>
        <div className={styles.formWrapper}>
          <h2>
            {action_type === "edit"
              ? "Edit"
              : action_type === "cancel"
                ? "Cancel"
                : action_type === "exit"
                  ? "Exit"
                  : "Create"}{" "}
            {selectedTradeType} Trades
          </h2>
          {isEditing && (
            <h4 className={styles.blinkingText}>
              {action_type === "edit" && "Editing Mode!!"}
              {action_type === "cancel" && "Cancel Mode!!"}
              {action_type === "exit" && "Exit Mode!!"}
            </h4>
          )}

          <div className={styles.formBody}>
            {/* SYMBOL NAME */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Symbol Name</p>

              <Autocomplete
                disabled={isEditing}
                options={displaySymbolsList.map((item) => item.name)}
                value={symbolName || null}
                onChange={(_, newValue) =>
                  formInputOnChange({ target: { name: "symbolName", value: newValue || "" } })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search symbol..."
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        fontSize: 13,
                        background: "#fff",
                        "& fieldset": { border: "none" },
                      },
                    }}
                  />
                )}
                sx={{ width: "70%" }}
                ListboxProps={{ style: { maxHeight: 220, fontSize: 13 } }}
                noOptionsText="No symbols found"
              />
            </div>

            {/* LTP DISPLAY (only for cash) */}
            {selectedTradeType === "cash" && (
              <div className={styles.inputControlBox}>
                <p className={styles.label} style={{ minWidth: 190, flexShrink: 0 }}>LTP (Last Traded Price)</p>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                  <input
                    disabled={true}
                    type="text"
                    name="ltp"
                    id="ltp"
                    value={ltp ? `₹${parseFloat(ltp).toFixed(2)}` : "Fetching..."}
                    readOnly
                    style={{ backgroundColor: "#f0f0f0", flex: 1, margin: 0 }}
                  />
                  <button
                    type="button"
                    onClick={refreshLtp}
                    disabled={ltpRefreshing || !symbolName}
                    title="Refresh LTP"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 30, height: 30, borderRadius: 6, border: "1px solid #cbd5e1",
                      background: "#fff", cursor: ltpRefreshing || !symbolName ? "not-allowed" : "pointer",
                      flexShrink: 0, opacity: !symbolName ? 0.4 : 1,
                    }}
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="#016551" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ transform: ltpRefreshing ? "rotate(360deg)" : "none", transition: "transform 0.6s" }}
                    >
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* LIVE LTP (websocket) — cash */}
            {selectedTradeType === "cash" && (
              <div className={styles.inputControlBox}>
                <p className={styles.label} style={{ minWidth: 190, flexShrink: 0 }}>Live LTP</p>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, flexWrap: "wrap" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: wsConnected ? "#22c55e" : "#cbd5e1", display: "inline-block" }} />
                  <span style={{ fontSize: 13, color: wsConnected && liveLtp != null ? "#016551" : "#94a3b8", fontWeight: 600 }}>
                    {liveLtp != null ? `₹${liveLtp.toFixed(2)}` : "—"}
                  </span>
                </div>
              </div>
            )}

            {/* LTP DISPLAY (only for futures) */}
            {isFuturesType(selectedTradeType) && (
              <div className={styles.inputControlBox}>
                <p className={styles.label} style={{ minWidth: 190, flexShrink: 0 }}>LTP (Last Traded Price)</p>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                  <input
                    disabled={true}
                    type="text"
                    name="ltp"
                    id="ltp"
                    value={ltp ? `₹${parseFloat(ltp).toFixed(2)}` : 0}
                    readOnly
                    style={{ backgroundColor: "#f0f0f0", flex: 1, margin: 0 }}
                  />
                  <button
                    type="button"
                    onClick={refreshLtp}
                    disabled={ltpRefreshing || !symbolName || !selectedExpiryDate}
                    title="Refresh LTP"
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      width: 30, height: 30, borderRadius: 6, border: "1px solid #cbd5e1",
                      background: "#fff", cursor: ltpRefreshing || !symbolName ? "not-allowed" : "pointer",
                      flexShrink: 0, opacity: !symbolName ? 0.4 : 1,
                    }}
                  >
                    <svg
                      width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke="#016551" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                      style={{ transform: ltpRefreshing ? "rotate(360deg)" : "none", transition: "transform 0.6s" }}
                    >
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* LIVE LTP (websocket) — futures / future-pro */}
            {isFuturesType(selectedTradeType) && (
              <div className={styles.inputControlBox}>
                <p className={styles.label} style={{ minWidth: 190, flexShrink: 0 }}>Live LTP</p>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flex: 1, flexWrap: "wrap" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: wsConnected ? "#22c55e" : "#cbd5e1", display: "inline-block" }} />
                  <span style={{ fontSize: 13, color: wsConnected && liveLtp != null ? "#016551" : "#94a3b8", fontWeight: 600 }}>
                    {liveLtp != null ? `₹${liveLtp.toFixed(2)}` : "—"}
                  </span>
                </div>
              </div>
            )}

            {/* SEGMENT SELECTION */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Select Segment: </p>

              <select
                disabled={isEditing}
                className={styles.selectedSymbolInput}
                name="segment"
                value={segment}
                onChange={formInputOnChange}
              >
                {segmentOptions.map((item) => {
                  return (
                    <option key={item.id} value={item.value}>
                      {item.label}
                    </option>
                  );
                })}

                {isFuturesType(selectedTradeType) ? (
                  <option key={4} value="STBT">
                    STBT
                  </option>
                ) : null}
              </select>
            </div>

            {/* TRADE TYPE (Future Pro only, fixed to "Pro") */}
            {selectedTradeType === "future-pro" && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Trade Type</p>
                <input
                  readOnly
                  className={styles.selectedSymbolInput}
                  name="trade_type"
                  value={trade_type || "Pro"}
                />
              </div>
            )}

            {/* SIGNAL SELECTION */}
            {segment === "INTRADAY" ||
            segment === "SWING" ||
            (selectedTradeType === "future-pro" && (segment === "STBT" || segment === "BTST")) ? (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Select Signal: </p>

                <select
                  disabled={
                    isEditing ||
                    (segment === "SWING" && selectedTradeType === "cash")
                  }
                  // disabled={!(selectedTradeType === "cash" && segment === "INTRADAY")}
                  className={styles.selectedSymbolInput}
                  name="signal"
                  value={signal}
                  onChange={formInputOnChange}
                >
                  <option value="" disabled>
                    -- Select Signal --
                  </option>
                  {(
                    signalOptions[selectedTradeType] || signalOptions.default
                  ).map((item) => {
                    return (
                      <option key={item.id} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            ) : null}

            {/* <fieldset>
            <legend>Select Advisory Type:</legend>

            {advisoryTypeOptions.map((option) => (
              <div>
                <input
                  disabled={isEditing}
                  style={{
                    margin: "0.8rem",
                  }}
                  type="radio"
                  id={option.value}
                  name="advisoryType"
                  value={option.value}
                  checked={advisoryType === option.value}
                  onChange={formInputOnChange}
                />
                <label for={option.value}>{option.label}</label>
              </div>
            ))}
          </fieldset> */}

            {/* ADVISORY DURATION not needed for cash, futures */}
            {/* <div className={styles.inputControlBox}>
            <p className={styles.label}>DURATION</p>

            <div className={styles.advisoryDuration}>
              <p>{ADVISORY_DURATION}</p>
            </div>
          </div> */}

            {/* PRICE RANGE not needed for cash, futures */}
            {/* <div className={styles.inputControlBox}>
            <p className={styles.label}>Price Range*</p>
            <input
              disabled={isEditing}
              className={styles.entryPriceInput}
              required
              type="text"
              name="priceRange"
              id="priceRange"
              placeholder="eg. ₹125-₹150"
              value={priceRange}
              onChange={formInputOnChange}
            />
          </div> */}

            {/* ENTRY / SUGGESTED PRICE */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Entry Price*</p>
              <input
                disabled={isEditing || call_type === "CMP"}
                className={styles.entryPriceInput}
                required
                type="text"
                name="entryPrice"
                id="entryPrice"
                value={entryPrice}
                onChange={formInputOnChange}
              />
            </div>

            {/* TARGET PRICE - Show for Create, Edit, or Exit action */}
            {(!isEditing ||
              action_type === "edit" ||
              action_type === "exit") && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Target Price*</p>
                <input
                  type="text"
                  required
                  name="targetPrice"
                  id="targetPrice"
                  value={targetPrice}
                  onChange={formInputOnChange}
                  disabled={action_type === "exit"}
                  style={
                    action_type === "exit" ? { backgroundColor: "#f0f0f0" } : {}
                  }
                />
              </div>
            )}

            {/* STOP LOSS - Show for Create OR Edit action only */}
            {(!isEditing || action_type === "edit") && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Stop Loss</p>
                <input
                  type="text"
                  name="stopLoss"
                  id="stopLoss"
                  value={stopLoss}
                  onChange={formInputOnChange}
                />
              </div>
            )}

            {/* P/L SUMMARY (target + stoploss) */}
            {(() => {
                const entry = parseFloat(entryPrice);
                const target = parseFloat(targetPrice);
                const sl = parseFloat(stopLoss);
                const ltpVal = parseFloat(ltp);

                if (selectedTradeType === "cash") {
                  const basePrice = call_type === "CMP" ? ltpVal : entry;
                  if (!basePrice || isNaN(basePrice)) return null;

                  const isBuy = signal !== "Sell";
                  const rewardPct = target
                    ? (isBuy ? (target - basePrice) / basePrice : (basePrice - target) / basePrice) * 100
                    : null;
                  const riskPct = sl
                    ? (isBuy ? (sl - basePrice) / basePrice : (basePrice - sl) / basePrice) * 100
                    : null;
                  if (rewardPct === null && riskPct === null) return null;

                  return (
                    <div className={styles.inputControlBox}>
                      <p className={styles.label}>P/L Summary</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "70%" }}>
                        {rewardPct !== null && (
                          <div style={pnlBoxStyle(rewardPct >= 0)}>
                            <span style={{ fontSize: 11, opacity: 0.75 }}>Target</span>
                            <span style={{ fontWeight: 700 }}>
                              {rewardPct >= 0 ? "+" : ""}{rewardPct.toFixed(2)}%
                            </span>
                          </div>
                        )}
                        {riskPct !== null && (
                          <div style={pnlBoxStyle(false)}>
                            <span style={{ fontSize: 11, opacity: 0.75 }}>Stoploss Risk</span>
                            <span style={{ fontWeight: 700 }}>{riskPct.toFixed(2)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                if (isFuturesType(selectedTradeType)) {
                  const basePrice = call_type === "CMP" ? ltpVal : entry;
                  if (!basePrice || isNaN(basePrice)) return null;

                  const lot = parseFloat(lotSize) || 1;
                  const isLong = signal !== "short";
                  const rewardAbs = target
                    ? (isLong ? target - basePrice : basePrice - target) * lot
                    : null;
                  const riskAbs = sl
                    ? (isLong ? sl - basePrice : basePrice - sl) * lot
                    : null;
                  if (rewardAbs === null && riskAbs === null) return null;

                  return (
                    <div className={styles.inputControlBox}>
                      <p className={styles.label}>P/L Summary</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "70%" }}>
                        {rewardAbs !== null && (
                          <div style={pnlBoxStyle(rewardAbs >= 0)}>
                            <span style={{ fontSize: 11, opacity: 0.75 }}>Target</span>
                            <span style={{ fontWeight: 700 }}>
                              {rewardAbs >= 0 ? "+" : "-"}₹{Math.abs(rewardAbs).toFixed(2)}
                            </span>
                          </div>
                        )}
                        {riskAbs !== null && (
                          <div style={pnlBoxStyle(riskAbs >= 0)}>
                            <span style={{ fontSize: 11, opacity: 0.75 }}>Stoploss Risk</span>
                            <span style={{ fontWeight: 700 }}>
                              {riskAbs >= 0 ? "+" : "-"}₹{Math.abs(riskAbs).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              })()}

            {/* ========== CANCEL ACTION FIELDS ========== */}
            {isEditing && action_type === "cancel" && (
              <>
                {/* TRADE STATUS for Cancel */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Trade Status</p>
                  <select
                    name="trade_status"
                    value={tradesFormInput.trade_status}
                    onChange={formInputOnChange}
                    className={styles.selectedSymbolInput}
                    disabled={true}
                  >
                    <option value="CANCEL">CANCEL</option>
                  </select>
                </div>

                {/* CANCEL REASON */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Cancel Reason*</p>
                  <input
                    name="cancel_reason"
                    placeholder="Enter cancel reason..."
                    value={tradesFormInput.cancel_reason || ""}
                    onChange={formInputOnChange}
                    required
                  />
                </div>
              </>
            )}

            {/* ========== EXIT ACTION FIELDS ========== */}
            {isEditing && action_type === "exit" && (
              <>
                {/* EXIT PRICE */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Exit Price*</p>
                  <input
                    ref={exitPriceRef}
                    type="text"
                    required
                    name="exitPrice"
                    id="exitPrice"
                    value={exitPrice}
                    onChange={formInputOnChange}
                  />
                </div>

                {/* P&L RESULT (cash + futures) */}
                {(selectedTradeType === "cash" ||
                  isFuturesType(selectedTradeType)) &&
                  (pnlLoading || pnlData) && (
                    <div className={styles.inputControlBox}>
                      <p className={styles.label}>P&amp;L</p>
                      {pnlLoading ? (
                        <span
                          style={{
                            width: "58%",
                            fontSize: 12,
                            color: "#94a3b8",
                          }}
                        >
                          Fetching...
                        </span>
                      ) : (
                        <span
                          style={{
                            width: "58%",
                            fontSize: 13,
                            fontWeight: 700,
                            color:
                              parseFloat(pnlData) >= 0 ? "#166534" : "#dc2626",
                            background:
                              parseFloat(pnlData) >= 0 ? "#f0fdf4" : "#fef2f2",
                            border: `1px solid ${parseFloat(pnlData) >= 0 ? "#bbf7d0" : "#fecaca"}`,
                            borderRadius: 8,
                            padding: "5px 8px",
                            display: "inline-block",
                          }}
                        >
                          ₹{pnlData || 0}
                          {selectedTradeType === "cash" ? "%" : ""}
                        </span>
                      )}
                    </div>
                  )}
              </>
            )}

            {/* CALL TYPE (BID / CMP) */}
            {["cash", "futures", "future-pro"].includes(selectedTradeType) && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Call Type</p>

                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="call_type"
                      value="BID"
                      checked={call_type === "BID"}
                      onChange={formInputOnChange}
                    />
                    BID
                  </label>

                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="call_type"
                      value="CMP"
                      checked={call_type === "CMP"}
                      onChange={formInputOnChange}
                    />
                    CMP
                  </label>
                </div>
              </div>
            )}

            {/* BID TYPE — cash/futures segment, only when Call Type is BID */}
            {["cash", "futures", "future-pro"].includes(selectedTradeType) && call_type === "BID" && (
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Bid Type</p>
                <select
                  className={styles.selectedSymbolInput}
                  name="bid_type"
                  value={bid_type || ""}
                  onChange={formInputOnChange}
                >
                  <option value="" disabled>
                    -- Select Bid Type --
                  </option>
                  <option value="UPPER">Upper</option>
                  <option value="DOWN">Down</option>
                </select>
              </div>
            )}

            {/* LOT SIZE AND LOT EXPIRY */}
            {isFuturesType(selectedTradeType) ? (
              <>
                {/* LOT EXPIRY DROPDOWN */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Lot Expiry*</p>
                  <select
                    disabled={isEditing || expiryDatesList.length === 0}
                    className={styles.selectedSymbolInput}
                    name="lotExpiry"
                    value={selectedExpiryDate}
                    onChange={handleExpiryChange}
                  >
                    {expiryDatesList.length === 0 ? (
                      <option value="">Loading expiry dates...</option>
                    ) : (
                      expiryDatesList.map((date, index) => (
                        <option key={index} value={date}>
                          {date}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* LOT SIZE (AUTO-FILLED) */}
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Lot Size</p>
                  <input
                    disabled={true}
                    type="text"
                    name="lotSize"
                    id="lotSize"
                    value={lotSize}
                    placeholder="Select symbol to auto-fill..."
                    onChange={formInputOnChange}
                    style={{ backgroundColor: "#f0f0f0" }}
                  />
                </div>
              </>
            ) : null}

            {/* DESCRIPTION NOT NEEDED IN TRADES, PLS REMOVE LATER */}
            {/* <div className={styles.inputControlBox}>
            <p className={styles.label}>Description</p>
            <textarea
              name="description"
              id="description"
              placeholder="Description for Admin Panel..."
              value={description}
              onChange={formInputOnChange}
            ></textarea>
           </div> */}
            {selectedTradeType === "futures" && <>
              {/* ENABLE PARRAVA TOGGLE */}
              {/* <div className={styles.inputControlBox}>
                <p className={styles.label}>Enable Parrava</p>
                <Switch
                  checked={!!valid_for_parrva}
                  onChange={handleParravaToggle}
                  color="success"
                  inputProps={{ "aria-label": "enable parrava" }}
                />
              </div> */}

              {/* INSTRUMENT SELECTION */}
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Instrument</p>
                <select
                  className={styles.selectedSymbolInput}
                  name="instrument"
                  value={instrument || ""}
                  onChange={formInputOnChange}
                >
                  <option value="" disabled>
                    -- Select Instrument --
                  </option>
                  {instrumentOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* ENTRY PRICE RANGE SELECTION */}
              <div className={styles.inputControlBox}>
                <p className={styles.label}>Entry Price Range</p>
                <select
                  className={styles.selectedSymbolInput}
                  name="entry_price_range"
                  value={entry_price_range || ""}
                  onChange={formInputOnChange}
                >
                  <option value="" disabled>
                    -- Select Entry Price Range --
                  </option>
                  {entryPriceRangeOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

            </>}

            {selectedTradeType === "cash" && <>
              {/* Enable Parrva toggle now lives next to "Select Plan" (MultiPlansSelectCheckBox) */}

              {/* PARRVA CALL TYPE — only relevant once Parrva is enabled */}
              {valid_for_parrva && (
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Parrva Call Type</p>
                  <select
                    className={styles.selectedSymbolInput}
                    name="parrva_call_type"
                    value={parrva_call_type || ""}
                    onChange={formInputOnChange}
                  >
                    <option value="" disabled>
                      -- Select Call Type --
                    </option>
                    {parravaCallTypeOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* STOCK HORIZON — trade duration in days, only relevant once Parrva is enabled */}
              {valid_for_parrva && (
                <div className={styles.inputControlBox}>
                  <p className={styles.label}>Stock Horizon (Days)</p>
                  <input
                    type="number"
                    min="0"
                    name="stock_horizon"
                    id="stock_horizon"
                    value={stock_horizon || ""}
                    placeholder="Number of days for the trade"
                    onChange={formInputOnChange}
                  />
                </div>
              )}
            </>}

            {/* FILE UPLOAD */}
            <div className={styles.inputControlBox}>
              <p className={styles.label}>File*</p>
              <input
                // disabled={isEditing}
                // required
                type="file"
                name="fileInput"
                accept="application/pdf, .pdf"
                onChange={fileInputOnChange}
              />
            </div>

            <div className={styles.btnContainer}>
              <button
                className={styles.submitBtn}
                onClick={handleViewSummary}
              >
                view summary
              </button>

              <button
                className={styles.clearBtn}
                onClick={clearTradesFormInput}
              >
                clear
              </button>

              {isEditing ? (
                <button
                  // disabled={DISABLE_SUBMIT_BTN}
                  className={styles.cancelEditBtn}
                  onClick={handleCancelEdit}
                >
                  {action_type === "exit" ? "cancel exit" : "cancel edit"}
                </button>
              ) : null}
            </div>
          </div>
          {/* formBody end */}
        </div>

        {/* multi plans Select component */}
        <div
          style={{
            display: isEditing ? "none" : "block",
          }}
        >
          <MultiPlansSelectCheckBox
            selectedPlans={selectedPlans}
            setSelectedPlans={setSelectedPlans}
            showParravaToggle={selectedTradeType === "futures" || selectedTradeType === "cash"}
            validForParrva={valid_for_parrva}
            onParravaToggle={handleParravaToggle}
          />

          {/* {isEditing ? null : (
            <MultiPlansSelectCheckBox
              selectedPlans={selectedPlans}
              setSelectedPlans={setSelectedPlans}
            />
          )} */}
        </div>
      </div>
      {/* outFormWrapper end DIV */}

      {/* summary modal */}
      <MuiModal
        open={openAdvisorySummaryModal}
        setOpen={setOpenAdvisorySummaryModal}
      >
        <TradeCallsFormSummaryModal
          tradesFormInput={tradesFormInput}
          //   advisoryDuration={ADVISORY_DURATION}
          fileInput={fileInput}
          handlePublish={handlePublish}
          disabled={DISABLE_UPDATE_BTN}
          handleUpdate={handleUpdate}
          handleCloseModal={handleCloseModal}
          formattedSelectedPlans={formattedSelectedPlans}
          isEditing={isEditing}
          selectedTradeType={selectedTradeType}
          action_type={action_type}
        />
      </MuiModal>

    </div>
  );
};

export default TradeCallsForm;

// FETCHER FUNCTION SAMPLE
const fetchSymbolsList = async () => {
  const res = await axiosConfig({
    method: "get",
    url: "/home/symbols/list",
    data: {},
  });

  return res.data;
};

// FETCH FUTURES SYMBOLS LIST
const fetchFutureSymbolsList = async () => {
  const res = await axiosConfig({
    method: "get",
    url: "/option-trade-symbol-list/",
    data: {},
  });

  return res.data;
};

// FETCH EXPIRY DATES FOR A SPECIFIC SYMBOL
const fetchExpiryDates = async (symbol) => {
  const res = await axiosConfig({
    method: "post",
    url: "/optionchain/expirydate",
    data: { symbol },
  });

  return res.data;
};

// FETCH FUTURES SYMBOL DETAILS (fut_symbol and LTP)
const fetchFutSymbolDetails = async (symbol, expiry) => {
  const res = await axiosConfig({
    method: "get",
    url: `/create-fut-symbol-with-lot?symbol=${symbol}&expiry=${expiry}`,
    data: {},
  });

  return res.data;
};

// function for both 'POST' request
const postTradesForm = async (
  user_id,
  trades_form_input,
  file_input,
  selected_plans,
  selected_trade_type,
) => {
  const {
    symbolName,
    segment,
    signal,
    entryPrice,
    targetPrice,
    stopLoss,
    lotSize,
    lotExpiry,
    call_type,
    bid_type,
    fut_symbol,
    entry_price_range,
    valid_for_parrva,
    parrva_call_type,
    stock_horizon,
    instrument,
    trade_type,
  } = trades_form_input;
  console.log(trades_form_input, "Pooja");

  console.log(selected_trade_type, "selected_trade_type");
  console.log(typeof lotSize, "ppp");
  console.log(typeof targetPrice, "ppp1");
  console.log(typeof entryPrice, "ppp2");
  console.log(typeof stopLoss, "ppp4");

  const formData = new FormData();
  formData.append("file", file_input);
  formData.append("symbol", symbolName);
  formData.append("segment", segment);
  formData.append("signal", signal);
  formData.append("entry_price", entryPrice);
  formData.append("target_price", targetPrice);
  formData.append("stop_loss", stopLoss);
  formData.append("visible_to", selected_plans);
  if (selected_trade_type === "cash") {
    formData.append("call_type", call_type);
    formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
    formData.append("valid_for_parrva", !!valid_for_parrva);
    formData.append("parrva_call_type", valid_for_parrva ? parrva_call_type || "" : "");
    formData.append("stock_horizon", stock_horizon || "");
  }

  if (isFuturesType(selected_trade_type)) {
    formData.append("lot_size", lotSize);
    formData.append("lot_expiry", lotExpiry);
    if (fut_symbol) {
      formData.append("fut_symbol", fut_symbol);
    }
    // Future Pro sends trade_type instead of parrva / instrument / entry_price_range
    if (selected_trade_type === "future-pro") {
      formData.append("trade_type", trade_type || "Pro");
      formData.append("call_type", call_type);
      formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
    } else {
      formData.append("entry_price_range", entry_price_range);
      formData.append("valid_for_parrva", !!valid_for_parrva);
      formData.append("instrument", instrument || "");
      formData.append("call_type", call_type);
      formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
    }
  }
  const res = await axiosConfig({
    method: "post",
    url: `/predictions/call/${user_id}/${selected_trade_type}`,
    data: formData,
  });

  return res.data;
};

// function for patch request (edit form)
const updateTradesForm = async (
  user_id,
  advisory_form_input,
  file_input,
  active_advisory_obj,
  selected_trade_type,
) => {
  const { exitPrice, stopLoss, targetPrice, cancel_reason, action_type, valid_for_parrva, parrva_call_type, stock_horizon, call_type, bid_type } =
    advisory_form_input;
  const { id: trade_id } = active_advisory_obj;

  const formData = new FormData();
  formData.append("user_id", user_id);
  formData.append("call_id", trade_id);

  // EDIT action: Only send changed target_price and stop_loss
  if (action_type === "edit") {
    if (targetPrice && targetPrice !== active_advisory_obj.target_price) {
      formData.append("target_price", targetPrice);
    }
    if (stopLoss && stopLoss !== active_advisory_obj.stop_loss) {
      formData.append("stop_loss", stopLoss);
    }
    if (file_input) {
      formData.append("file", file_input);
    }
    if (selected_trade_type === "futures") {
      formData.append("valid_for_parrva", !!valid_for_parrva);
      formData.append("call_type", call_type);
      formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
    }
    if (selected_trade_type === "future-pro") {
      formData.append("call_type", call_type);
      formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
    }
    if (selected_trade_type === "cash") {
      formData.append("valid_for_parrva", !!valid_for_parrva);
      formData.append("parrva_call_type", valid_for_parrva ? parrva_call_type || "" : "");
      formData.append("stock_horizon", stock_horizon || "");
      formData.append("call_type", call_type);
      formData.append("bid_type", call_type === "BID" ? bid_type || "" : "");
    }
  }

  // CANCEL action: Only send trade_status and cancel_reason
  if (action_type === "cancel") {
    formData.append("trade_status", "CANCEL");
    if (cancel_reason) {
      formData.append("cancel_reason", cancel_reason);
    }
  }

  // EXIT action: Only send trade_status and exit_price
  if (action_type === "exit") {
    formData.append("trade_status", "CLOSED");
    if (exitPrice && exitPrice.trim() !== "") {
      const exitPriceNumber = parseFloat(exitPrice);
      formData.append("exit_price", exitPriceNumber);
    }
  }

  const res = await axiosConfig({
    method: "post",
    url: getTradeUpdateUrl(selected_trade_type),
    data: formData,
  });

  return res.data;
};
